import { getApi, getApiById, postApi, postApiWithoutBody, putApi, putApiWithoutId } from "../../Shared/Services/RestService";
import { PaginationResponse, PagedStatsResponse } from "../../Shared/Pagination/PaginationResponse";
import {
  GrowthCampaign,
  GrowthContent,
  GrowthEvent,
  GrowthFunnel,
  GrowthInvitation,
  GrowthOverview,
  GrowthSource,
  InvitationTrackResponse,
  InviteProspectRequest,
  LimitAlertSummary,
  LimitCandidateExhaustionResponse,
  LimitExhaustionFilters,
  LimitOutreachDraft,
  LimitOutreachHistoryPage,
  LimitOutreachRequest,
  LimitOutreachResult,
  LimitRecruiterExhaustionFilters,
  LimitRecruiterExhaustionResponse,
  PortalConfig,
  PrivateInvitationAdminRow,
  PrivateReferralStats,
  Prospect,
  ProspectRequest,
  EarlyAccessProspectConversion,
  ReferralAdminFilters,
  TopReferrer,
} from "../Types/GrowthType";
import { TrackEventRequest, TrackVisitorRequest } from "../Types/GrowthType";

export interface DateRangeParams {
  fromDate?: string | null;
  toDate?: string | null;
}

export interface ProspectFilterParams extends DateRangeParams {
  source?: string;
  campaign?: string;
  experienceYears?: number;
  title?: string;
  status?: string;
  invitationStatus?: string;
  registrationStatus?: string;
  page?: number;
  size?: number;
}

export const getGrowthOverview = (params?: DateRangeParams & { role?: string }) => {
  return getApi<GrowthOverview>("/admin/growth/overview", params);
};

export const getGrowthFunnel = (params?: DateRangeParams & { role?: string }) => {
  return getApi<GrowthFunnel>("/admin/growth/funnel", params);
};

export const getGrowthSources = (params?: DateRangeParams) => {
  return getApi<GrowthSource[]>("/admin/growth/sources", params);
};

export const getGrowthCampaigns = (params?: DateRangeParams) => {
  return getApi<GrowthCampaign[]>("/admin/growth/campaigns", params);
};

export const getGrowthContent = (params?: DateRangeParams) => {
  return getApi<GrowthContent[]>("/admin/growth/content", params);
};

export const getGrowthProspects = (params?: ProspectFilterParams) => {
  return getApi<PaginationResponse<Prospect>>("/admin/growth/prospects", params);
};

export const getGrowthProspectById = (id: string) => {
  return getApiById<Prospect>("/admin/growth/prospects", id);
};

export const createGrowthProspect = (payload: ProspectRequest) => {
  return postApi<Prospect>("/admin/growth/prospects", payload);
};

export const updateGrowthProspect = (id: string, payload: ProspectRequest) => {
  return putApi<Prospect>("/admin/growth/prospects", id, payload);
};

export const inviteGrowthProspect = (id: string, payload?: InviteProspectRequest) => {
  return postApi<GrowthInvitation>(`/admin/growth/prospects/${id}/invite`, payload || {});
};

/**
 * Converts an early-access (waitlist) signup into a Growth prospect and issues its
 * token-based invitation in one shot. The waitlist row is flipped to PROSPECT_SENT.
 */
export const convertEarlyAccessToProspect = (earlyAccessId: string) => {
  return postApiWithoutBody<EarlyAccessProspectConversion>(
    `/admin/growth/early-access/${earlyAccessId}/to-prospect`
  );
};

export interface InvitationFilterParams extends DateRangeParams {
  page?: number;
  size?: number;
  status?: string;
  search?: string;
}

export interface ReferralFilterParams extends DateRangeParams {
  search?: string;
  isRegistered?: boolean;
  isApproved?: boolean;
}

export const getGrowthInvitations = (params?: InvitationFilterParams) => {
  return getApi<PaginationResponse<GrowthInvitation>>("/admin/growth/invitations", params);
};

/**
 * Admin-wide referral overview: headline counts plus the paginated invite listing for the same
 * filtered set. One call so the cards and the listing can never disagree with each other.
 * Referrers are excluded because they page independently.
 */
export const getAdminReferralOverview = (
  filters?: ReferralAdminFilters,
  page: number = 0,
  size?: number
) => {
  const params: Record<string, unknown> = { page };

  if (filters?.status) params.status = filters.status;
  if (filters?.search) params.search = filters.search;
  if (filters?.isRegistered !== undefined) params.isRegistered = filters.isRegistered;
  if (filters?.isApproved !== undefined) params.isApproved = filters.isApproved;
  if (filters?.fromDate) params.fromDate = filters.fromDate;
  if (filters?.toDate) params.toDate = filters.toDate;
  if (size !== undefined) params.size = size;

  return getApi<PagedStatsResponse<PrivateInvitationAdminRow, PrivateReferralStats>>(
    "/users/invitation/admin/overview",
    params
  );
};

/**
 * Every candidate who has shared at least one link, ranked by how many invitees actually
 * registered. Served separately from the overview and paged on its own, so paging through
 * referrers does not also reset the invite listing underneath.
 */
export const getAdminTopReferrers = (
  search?: string,
  page: number = 0,
  size?: number
) => {
  const params: Record<string, unknown> = { page };

  if (search) params.search = search;
  if (size !== undefined) params.size = size;

  return getApi<PaginationResponse<TopReferrer>>(
    "/users/invitation/admin/referrers",
    params
  );
};

export const getGrowthEvents = (params?: DateRangeParams & { eventName?: string; page?: number; size?: number }) => {
  return getApi<PaginationResponse<GrowthEvent>>("/admin/growth/events", params);
};

export const trackPublicVisitor = (payload: TrackVisitorRequest) => {
  return postApi<void>("/public/track/visitor", payload);
};

export const trackPublicEvent = (payload: TrackEventRequest) => {
  return postApi<void>("/public/track/event", payload);
};

export const getPublicInvitation = (token: string) => {
  return getApi<InvitationTrackResponse>(`/public/invitation/${token}`);
};

export const trackInvitationOpen = (token: string) => {
  return postApi<InvitationTrackResponse>(`/public/invitation/${token}/open`, {});
};

export const trackInvitationClick = (token: string) => {
  return postApi<InvitationTrackResponse>(`/public/invitation/${token}/click`, {});
};

export const trackInvitationAccept = (token: string) => {
  return postApi<InvitationTrackResponse>(`/public/invitation/${token}/accept`, {});
};

/** Public: the waitlist / role rules the registration screen must honour. */
export const getPortalConfig = () => {
  return getApi<PortalConfig>("/portal-config");
};

/* ==========================================================================
   Credit exhaustion
   ========================================================================== */

const LIMITS = "/admin/growth/limits";

/**
 * Listings and drafts hand back a bare 32-character hex id, while the free-credit and outreach
 * bodies are bound to a UUID. Java happens to accept undashed hex today, but relying on that
 * leniency across three endpoints is a silent breakage waiting for a Jackson version bump, so the
 * dashes go back on here instead.
 */
const toUuid = (id: string): string => {
  const hex = (id || "").replace(/-/g, "").trim();
  if (hex.length !== 32) return id;
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-");
};

/** Badge counters for the Growth submenu. Read-only; does not trigger a scan. */
export const getLimitAlertSummary = () => {
  return getApi<LimitAlertSummary>(`${LIMITS}/summary`);
};

/**
 * Runs the detection sweep and raises any outstanding admin notifications.
 *
 * <p>A POST rather than folded into the GET listings because it writes. The screen calls this on
 * open so the numbers are current without waiting for the scheduled tick; the schedule is the safety
 * net, not the primary trigger.
 */
export const refreshLimitAlerts = () => {
  return postApiWithoutBody<LimitAlertSummary>(`${LIMITS}/refresh`);
};

export const getExhaustedCandidates = (
  filters?: LimitExhaustionFilters,
  page: number = 0,
  size?: number
) => {
  const params: Record<string, unknown> = { page };
  if (filters?.search) params.search = filters.search;
  if (filters?.sortBy) params.sortBy = filters.sortBy;
  if (size !== undefined) params.size = size;
  return getApi<LimitCandidateExhaustionResponse>(`${LIMITS}/candidates`, params);
};

export const getExhaustedRecruiters = (
  filters?: LimitRecruiterExhaustionFilters,
  page: number = 0,
  size?: number
) => {
  const params: Record<string, unknown> = { page };
  if (filters?.search) params.search = filters.search;
  if (filters?.sortBy) params.sortBy = filters.sortBy;
  if (filters?.exhaustionType && filters.exhaustionType !== "ALL") {
    params.exhaustionType = filters.exhaustionType;
  }
  if (size !== undefined) params.size = size;
  return getApi<LimitRecruiterExhaustionResponse>(`${LIMITS}/recruiters`, params);
};

/** The pre-filled draft the outreach modal opens with, ready to be edited before sending. */
export const getLimitOutreachDraft = (userId: string, exhaustionType?: string) => {
  const params: Record<string, unknown> = {};
  if (exhaustionType) params.exhaustionType = exhaustionType;
  return getApi<LimitOutreachDraft>(`${LIMITS}/outreach/${toUuid(userId)}/draft`, params);
};

export const sendLimitOutreach = (payload: LimitOutreachRequest) => {
  return postApi<LimitOutreachResult>(`${LIMITS}/outreach`, {
    ...payload,
    userId: toUuid(payload.userId),
  });
};

export const getLimitOutreachHistory = (
  search?: string,
  page: number = 0,
  size?: number
) => {
  const params: Record<string, unknown> = { page };
  if (search) params.search = search;
  if (size !== undefined) params.size = size;
  return getApi<LimitOutreachHistoryPage>(`${LIMITS}/outreach/history`, params);
};

/**
 * Free credits for an exhausted user. Separate from the general admin top-up because this one also
 * closes the exhaustion alert, which is what makes the giveaway measurable and moves the person off
 * the list.
 */
export const grantFreeCredits = (payload: {
  userId: string;
  creditType: 'INVITE' | 'JOB' | 'BOTH';
  inviteCredits: number;
  jobCredits: number;
}) => {
  return postApi<{ message: string; inviteCountRemaining: number; jobCredits: number | null }>(
    `${LIMITS}/free-credits`,
    { ...payload, userId: toUuid(payload.userId) }
  );
};

/** Admin only: persists the portal configuration. */
export const updatePortalConfig = (payload: Partial<PortalConfig>) => {
  return putApiWithoutId<PortalConfig>("/portal-config", payload);
};
